import React, { useState, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FolderOpen, Upload, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

interface BulkUploadSectionProps {
  onUploadStart?: (files: FileList) => void;
  onUploadComplete?: (sessionId: string) => void;
  className?: string;
}

interface UploadSession {
  id: string;
  status: 'uploading' | 'processing' | 'review' | 'complete';
  totalFiles: number;
  processedFiles: number;
  folderName: string;
  createdAt: Date;
}

const BulkUploadSection: React.FC<BulkUploadSectionProps> = ({
  onUploadStart,
  onUploadComplete,
  className
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadSession, setUploadSession] = useState<UploadSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setError(null);

    const items = Array.from(e.dataTransfer.items);
    
    // Check if any items are directories
    const hasDirectories = items.some(item => item.webkitGetAsEntry()?.isDirectory);
    
    if (!hasDirectories) {
      setError('Please drop a folder containing your sequence files.');
      return;
    }

    // Process the directory upload
    processDirectoryUpload(items);
  };

  const handleFolderSelect = () => {
    // For now, trigger the file input (HTML5 doesn't directly support folder selection in all browsers)
    // In a real implementation, you'd want to use the webkitdirectory attribute
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFileUpload(files);
    }
  };

  const processDirectoryUpload = async (items: DataTransferItem[]) => {
    try {
      // Mock implementation - in reality, you'd implement proper folder traversal
      const folderName = 'Uploaded Show Folder';
      const mockSession: UploadSession = {
        id: `bulk_${Date.now()}`,
        status: 'uploading',
        totalFiles: 25, // Mock value
        processedFiles: 0,
        folderName,
        createdAt: new Date()
      };

      setUploadSession(mockSession);
      onUploadStart?.(new FileList());

      // Simulate upload progress
      simulateUploadProgress(mockSession);
    } catch (err) {
      setError('Failed to process folder upload. Please try again.');
    }
  };

  const processFileUpload = async (files: FileList) => {
    try {
      const folderName = 'Selected Files';
      const mockSession: UploadSession = {
        id: `bulk_${Date.now()}`,
        status: 'uploading',
        totalFiles: files.length,
        processedFiles: 0,
        folderName,
        createdAt: new Date()
      };

      setUploadSession(mockSession);
      onUploadStart?.(files);

      // Simulate upload progress
      simulateUploadProgress(mockSession);
    } catch (err) {
      setError('Failed to process file upload. Please try again.');
    }
  };

  const simulateUploadProgress = (session: UploadSession) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 3;
      
      setUploadSession(prev => prev ? {
        ...prev,
        processedFiles: Math.min(Math.floor(progress), session.totalFiles)
      } : null);

      if (progress >= session.totalFiles) {
        clearInterval(interval);
        setUploadSession(prev => prev ? {
          ...prev,
          status: 'processing',
          processedFiles: session.totalFiles
        } : null);
        
        // Simulate processing completion
        setTimeout(() => {
          setUploadSession(prev => prev ? {
            ...prev,
            status: 'review'
          } : null);
          onUploadComplete?.(session.id);
        }, 3000);
      }
    }, 500);
  };

  const getStatusMessage = (session: UploadSession) => {
    switch (session.status) {
      case 'uploading':
        return `Uploading files... ${session.processedFiles}/${session.totalFiles}`;
      case 'processing':
        return 'Processing sequences and extracting metadata...';
      case 'review':
        return 'Upload complete! We\'ll email you a preview for approval within 24-48 hours.';
      case 'complete':
        return 'Your sequences are now live on the marketplace!';
      default:
        return 'Processing...';
    }
  };

  const getStatusIcon = (session: UploadSession) => {
    switch (session.status) {
      case 'uploading':
        return <Upload className="h-5 w-5 text-blue-600" />;
      case 'processing':
        return <Clock className="h-5 w-5 text-orange-600" />;
      case 'review':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'complete':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      default:
        return <Clock className="h-5 w-5 text-gray-600" />;
    }
  };

  if (uploadSession) {
    return (
      <div className={cn('space-y-6', className)}>
        <Card>
          <CardContent className="p-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-full mx-auto mb-6 flex items-center justify-center">
                {getStatusIcon(uploadSession)}
              </div>
              <h3 className="text-2xl font-semibold mb-2">{uploadSession.folderName}</h3>
              <p className="text-muted-foreground mb-6">
                {getStatusMessage(uploadSession)}
              </p>
              
              {uploadSession.status === 'uploading' && (
                <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                  <div 
                    className="bg-primary h-3 rounded-full transition-all duration-300"
                    style={{ width: `${(uploadSession.processedFiles / uploadSession.totalFiles) * 100}%` }}
                  />
                </div>
              )}
              
              {uploadSession.status === 'review' && (
                <Alert className="mt-6">
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    Great! Your bulk upload is complete. Our team will review and organize your sequences, 
                    then send you a preview for approval. You'll receive an email within 24-48 hours.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      <Card>
        <CardContent className="p-12">
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-12 text-center transition-all",
              isDragging ? "border-primary bg-primary/5" : "border-gray-300",
              "hover:border-primary hover:bg-primary/5 dark:hover:bg-primary/10"
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full mx-auto mb-6 flex items-center justify-center">
              <FolderOpen className="h-10 w-10 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-2xl font-semibold mb-2">Drag & drop your show folder</h3>
            <p className="text-muted-foreground mb-6">
              Upload your entire 2024 Christmas or Halloween show directory
            </p>
            <Button size="lg" onClick={handleFolderSelect}>
              <FolderOpen className="mr-2 h-5 w-5" />
              Select Folder
            </Button>
            <p className="text-sm text-muted-foreground mt-4">
              We'll organize all sequences, create optimized listings, and handle the metadata
            </p>
          </div>
          
          {/* Hidden file input for folder selection */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            webkitdirectory=""
            className="hidden"
            onChange={handleFileInputChange}
          />
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card>
        <CardContent className="p-8">
          <h3 className="text-2xl font-semibold text-center mb-8">
            How Our White Glove Service Works
          </h3>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary text-white rounded-full mx-auto mb-4 flex items-center justify-center font-bold">
                1
              </div>
              <h4 className="font-semibold mb-2">Upload Your Folder</h4>
              <p className="text-sm text-muted-foreground">
                Drag and drop your entire show directory containing all your sequences
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary text-white rounded-full mx-auto mb-4 flex items-center justify-center font-bold">
                2
              </div>
              <h4 className="font-semibold mb-2">We Organize Everything</h4>
              <p className="text-sm text-muted-foreground">
                Our team processes each sequence, adds metadata, and creates professional listings
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary text-white rounded-full mx-auto mb-4 flex items-center justify-center font-bold">
                3
              </div>
              <h4 className="font-semibold mb-2">Review & Approve</h4>
              <p className="text-sm text-muted-foreground">
                We'll send you a preview of all listings for your approval before going live
              </p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary text-white rounded-full mx-auto mb-4 flex items-center justify-center font-bold">
                4
              </div>
              <h4 className="font-semibold mb-2">Start Earning</h4>
              <p className="text-sm text-muted-foreground">
                Your sequences go live on the marketplace and you start earning immediately
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BulkUploadSection;